<template>
	<picture :ref="lazyImageLabel" class="picture-element">
		<template v-if="!isPng">
			<source type="image/avif" v-bind="{ [isVisible ? 'srcset' : 'data-srcset']: avifImageUrl }" />
			<source type="image/webp" v-bind="{ [isVisible ? 'srcset' : 'data-srcset']: webpImageUrl }" />
		</template>

		<img
			class="provider-icon"
			:class="[
				{
					wide: isWideIcon,
					square: !isWideIcon,
				},
				size,
			]"
			:loading="lazy ? 'lazy' : 'eager'"
			:fetchpriority="lazy ? 'low' : 'auto'"
			:alt="providerClearName"
			:title="providerClearName"
			:src="imageUrl"
			@click="$emit('click')"
		/>
	</picture>
</template>

<script setup lang="ts">
import type { Package } from '@/@types/graphql-types';

import { useConstantStore } from '@/stores/pinia/useConstantStore';

import { lazyImageLabel, useLazyImage } from '@/features/ui/composables/useLazyImage';

const PROVIDER_FALLBACK_NAME = 'JustWatch';
const FALLBACK_ICON = 'https://static.justwatch.com/static/compile_jw/assets/jw-icon-badge.png';

const isClient = ref(false);

onMounted(() => {
	isClient.value = true;
});

const profiles = {
	icon: 's100',
	medium: 's50',
	small: 's40',
} as const;

const props = withDefaults(
	defineProps<{
		/** @deprecated */
		providerId?: Package['packageId'];
		providerShortName?: Package['shortName'];
		lazy?: boolean;
		size?: 'icon' | 'medium' | 'small';
		iconUrl?: string | null;
		/**
		 * Wide provider icons can be known just from the URL, they all contain "icon_wide"
		 * This prop is only to force a wide icon when using a custom URL like with SRs
		 */
		forceWide?: boolean;
		/**
		 * Force the use of the AVIF format for SRs by updating the hardcoded extension.
		 */
		forceAvif?: boolean;
	}>(),
	{
		providerId: 1,
		lazy: true,
		size: 'icon',
		iconUrl: null,
		forceWide: false,
		forceAvif: false,
	}
);

const constantStore = useConstantStore();

const { _el, isVisible } = useLazyImage(props.lazy);

const provider: Package | undefined = (() => {
	if (props.providerShortName != null) {
		return (constantStore.allProvidersByShortName as Record<Package['shortName'], Package>)[
			props.providerShortName
		];
	} else if (props.providerId != null) {
		return (constantStore.allProvidersById as Record<Package['shortName'], Package>)[props.providerId];
	}
	return undefined;
})();

const providerClearName = provider?.clearName ?? PROVIDER_FALLBACK_NAME;

const { jwConfig } = useRuntimeConfig().public;

const iconUrlRaw = (() => {
	let icon = props.iconUrl ?? provider?.icon;

	if (!icon) return FALLBACK_ICON;

	if (icon.startsWith('/')) {
		icon = `${jwConfig.IMAGESCALER_URL}${icon.replace('{profile}', profiles[props.size])}`;
	}

	return icon;
})();

const isWideIcon = (() => props.forceWide || iconUrlRaw.includes('icon_wide'))();

// in case the campaign icon override is PNG we don't render the webp and avif sources for transparency
const isPng = iconUrlRaw.endsWith('.png');

const imageUrl = iconUrlRaw.replace('{format}', 'jpeg');

const webpImageUrl = iconUrlRaw.replace('{format}', 'webp');
const avifImageUrl = (() => {
	if (props.forceAvif && !iconUrlRaw.includes('{format}')) {
		return iconUrlRaw.replace(/\.\w+$/, '.avif');
	}
	return iconUrlRaw.replace('{format}', 'avif');
})();
</script>

<style lang="scss">
.picture-element {
	display: inline-block;

	.provider-icon {
		&.square {
			border-radius: 20%;
			height: 50px;
			width: 50px;
			min-width: 50px;
			aspect-ratio: 1;

			&.icon {
				min-width: 50px;
			}

			&.medium {
				width: 40px;
				height: 40px;
				min-width: 40px;
			}

			&.small {
				width: 24px;
				height: 24px;
				border-radius: 4px;
				min-width: 24px;
			}
		}

		&.wide {
			border-radius: 8px;
			height: 32px;
			width: 80px;
			aspect-ratio: 80 / 32;
			min-width: 80px;
			min-height: 32px;
		}
	}
}
</style>
